import {
    MultiSelection,
    MultiSelectionEntity,
    PromoBlock,
    PromoSelection,
    SelectionMovie,
    ShowcaseSelection,
    WatchingEntity,
    ContinueWatchingSelection,
    StyledShowcasesSelectionEntity,
    StyledShowcasesSelection,
    ChannelProgramsSelection,
    TvChannel,
    TvChannelProgram,
    PromoSelectionItem,
} from '@funtech-int/kp-graphql-sdk/yango-movies-www';
import { AnalyticsSender, Offer } from '@funtech-int/yango-shared';

import {
    ChannelImpressionEntity,
    ContentMarkType,
    ImpressionWindowEntityType,
    MovieCardTabTitle,
    MyMoviesOfferEntityType,
    PaymentTypes,
    PromoOfferBadgeType,
    PromoType,
    SelectionEntryV2,
    SelectionsPages,
    ShowcasePages,
} from '@kinopoisk-int/evgen-web-events/web/named_enums';
import {
    myMoviesMovieImpressionMarkedV3,
    myMoviesMovieImpressionNavigatedV5,
    MyMoviesMovieImpressionNavigatedV5To,
    myMoviesMovieImpressionNavigatedV8,
    MyMoviesMovieImpressionNavigatedV8To,
    MyMoviesMovieImpressionNavigatedV8UuidType,
    myMoviesMovieImpressionShowedV4,
    myMoviesMovieImpressionShowedV6,
    MyMoviesMovieImpressionShowedV6UuidType,
    myMoviesMovieImpressionWithCardSelectedV2,
    myMoviesMovieImpressionWithCardShowedV2,
    myMoviesMultiselectionItemNavigatedV3,
    MyMoviesMultiselectionItemNavigatedV3To,
    myMoviesMultiselectionItemShowedV2,
    myMoviesSelectionNavigatedV3,
    MyMoviesSelectionNavigatedV3To,
    myMoviesShowed,
    myMoviesSubscriptionOfferNavigatedV5,
    myMoviesSubscriptionOfferShowedV5,
    myMoviesSubscriptionOfferSucceedV5,
    myMoviesTrailerPlayerPreviewFinishedV3,
    myMoviesTrailerPlayerPreviewStartedV3,
} from '@kinopoisk-int/evgen-web-events/web/my_movies';
import {
    multiselectionItemNavigatedV2,
    MultiselectionItemNavigatedV2EntityType,
    MultiselectionItemShowedNamespacesV2,
    multiselectionItemShowedV2,
    MultiselectionItemShowedV2EntityType,
} from '@kinopoisk-int/evgen-web-events/web/multiselection_item';
import {
    channelImpressionNavigated,
    channelImpressionShowed,
    ChannelImpressionShowedNamespaces,
} from '@kinopoisk-int/evgen-web-events/web/channel_impression';

import { normalizeItemPosition } from '@/shared/lib/analytics/selection';
import { transformSelectionType } from '@/shared/lib/analytics/showcase';
import { getBillingProductIds, getOfferPositionsData, getOfferTitle } from '@/shared/lib/analytics';

interface PromoBlockAnalytics {
    itemImpressionShowed(this: void): void;
    itemImpressionNavigated(
        this: void,
        to: 'payment' | 'player' | 'movie_card' | 'channel_player'
    ): void;
    movieImpressionMarked(
        this: void,
        type: 'bookmarked' | 'unbookmarked' | 'markedAsNotInteresting' | 'unmarkedAsNotInteresting'
    ): void;
    trailerPlayerPreviewStarted(this: void): void;
    trailerPlayerPreviewFinished(this: void): void;
    offerShowed(this: void, offer: Offer): void;
    offerNavigated(this: void, offer: Offer): void;
    offerSucceed(this: void, offer: Offer): void;
}

interface ChannelProgramsAnalytics {
    selectionNavigated(this: void, placement: SelectionNavigatedPlacement): void;
    channelImpressionShowed(
        this: void,
        channel: TvChannel,
        position: number,
        program?: TvChannelProgram | null
    ): void;
    channelImpressionNavigated(
        this: void,
        channel: TvChannel,
        position: number,
        program?: TvChannelProgram | null
    ): void;
}

type SelectionNavigatedPlacement = 'header' | 'last_item';

interface ShowcaseAnalytics {
    setSessionId(this: void, sessionId: string): void;
    showed(this: void): void;
    selectionNavigated(
        this: void,
        selection: ShowcaseSelection,
        position: number,
        placement: SelectionNavigatedPlacement
    ): void;
    cardClicked(
        this: void,
        selection: ShowcaseSelection,
        selectionPosition: number,
        movie: SelectionMovie,
        position: number
    ): void;
    cardShowed(
        this: void,
        selection: ShowcaseSelection,
        selectionPosition: number,
        movie: SelectionMovie,
        position: number
    ): void;
    multiSelectionCardShowed(
        this: void,
        multiSelection: MultiSelection,
        multiSelectionPosition: number,
        entity: MultiSelectionEntity,
        position: number
    ): void;
    multiSelectionCardNavigated(
        this: void,
        multiSelection: MultiSelection,
        multiSelectionPosition: number,
        entity: MultiSelectionEntity,
        position: number
    ): void;
    styledShowcaseCardShowed(
        this: void,
        multiSelection: StyledShowcasesSelection,
        multiSelectionPosition: number,
        entity: StyledShowcasesSelectionEntity,
        position: number
    ): void;
    styledShowcaseCardNavigated(
        this: void,
        multiSelection: StyledShowcasesSelection,
        multiSelectionPosition: number,
        entity: StyledShowcasesSelectionEntity,
        position: number
    ): void;
    promoSelectionCardShowed(
        this: void,
        promoSelection: PromoSelection,
        promoBlock: PromoBlock,
        position: number
    ): void;
    createPromoBlockAnalytics(
        this: void,
        promoSelection: PromoSelection,
        position: number
    ): PromoBlockAnalytics;
    continueWatchingCardShowed(
        this: void,
        continueWatching: ContinueWatchingSelection,
        entity: WatchingEntity,
        position: number
    ): void;
    continueWatchingCardNavigated(
        this: void,
        continueWatching: ContinueWatchingSelection,
        entity: WatchingEntity,
        position: number
    ): void;
    createChannelProgramsAnalytics(
        this: void,
        channelPrograms: ChannelProgramsSelection,
        selectionPosition: number
    ): ChannelProgramsAnalytics;
}

function mapSelectionNavigatedPlacement(placement: SelectionNavigatedPlacement): SelectionEntryV2 {
    switch (placement) {
        case 'header':
            return SelectionEntryV2.SelectionHeader;
        case 'last_item':
        default:
            return SelectionEntryV2.SelectionLastItem;
    }
}

export function createShowcaseAnalytics(eventSender: AnalyticsSender): ShowcaseAnalytics {
    let sessionId = '';

    return {
        setSessionId(id) {
            sessionId = id;
        },

        showed() {
            eventSender.sendOnce(myMoviesShowed, {});
        },

        selectionNavigated(selection, position, placement) {
            eventSender.send(myMoviesSelectionNavigatedV3, {
                entityType: mapSelectionNavigatedPlacement(placement),
                selectionId: selection.id,
                selectionName: selection.title ?? '-',
                selectionPosition: normalizeItemPosition(position),
                windowSessionId: sessionId ?? '-',
                to: MyMoviesSelectionNavigatedV3To.SelectionScreen,
            });
        },

        cardShowed(selection, selectionPosition, movie, position) {
            eventSender.sendOnce(myMoviesMovieImpressionWithCardShowedV2, {
                uuid: movie.contentId ?? '-',
                title: movie.title.toString() ?? '-',
                badgeType: PromoOfferBadgeType.NoBadge,
                cardPosition: normalizeItemPosition(position),
                selectionId: selection.id,
                selectionName: selection.title ?? '-',
                selectionPosition: normalizeItemPosition(selectionPosition),
                entityType: transformSelectionType(selection.typename ?? '-'),
                windowId: selection.showcaseId ?? '-',
                windowSessionId: sessionId ?? '-',
            });
        },

        cardClicked(selection, selectionPosition, movie, position) {
            eventSender.send(myMoviesMovieImpressionWithCardSelectedV2, {
                uuid: movie.contentId ?? '-',
                title: movie.title.toString() ?? '-',
                badgeType: PromoOfferBadgeType.NoBadge,
                cardPosition: normalizeItemPosition(position),
                selectionId: selection.id,
                selectionName: selection.title ?? '-',
                selectionPosition: normalizeItemPosition(selectionPosition),
                entityType: transformSelectionType(selection.typename ?? '-'),
                windowId: selection.showcaseId ?? '-',
                tabTitle: MovieCardTabTitle.None,
                windowSessionId: sessionId ?? '-',
            });
        },

        multiSelectionCardShowed(multiSelection, multiSelectionPosition, entity, position) {
            eventSender.sendOnce(myMoviesMultiselectionItemShowedV2, {
                windowId: multiSelection.showcaseId ?? '-',
                multiselectionId: multiSelection.key,
                multiselectionName: multiSelection.title ?? '-',
                multiselectionPosition: normalizeItemPosition(multiSelectionPosition),
                selectionId: `${entity.id}/${multiSelection.showcaseId}`,
                selectionName: entity.title ?? '-',
                selectionPosition: normalizeItemPosition(position),
                windowSessionId: sessionId ?? '-',
            });
        },

        multiSelectionCardNavigated(multiSelection, multiSelectionPosition, entity, position) {
            eventSender.send(myMoviesMultiselectionItemNavigatedV3, {
                windowId: multiSelection.showcaseId ?? '-',
                multiselectionId: multiSelection.key,
                multiselectionName: multiSelection.title ?? '-',
                multiselectionPosition: normalizeItemPosition(multiSelectionPosition),
                selectionId: `${entity.id}/${multiSelection.showcaseId}`,
                selectionName: entity.title ?? '-',
                selectionPosition: normalizeItemPosition(position),
                windowSessionId: sessionId ?? '-',
                to: MyMoviesMultiselectionItemNavigatedV3To.SelectionScreen,
            });
        },

        promoSelectionCardShowed(promoSelection, promoBlock, cardPosition) {
            eventSender.sendOnce(myMoviesMovieImpressionShowedV6, {
                entityType: ImpressionWindowEntityType.PROMO,
                uuid: promoBlock.contentId ?? '-',
                title: promoBlock.title.toString() ?? '-',
                uuidType: MyMoviesMovieImpressionShowedV6UuidType.Ott,
                cardPosition: normalizeItemPosition(cardPosition),
                windowId: promoSelection.showcaseId ?? '-',
                selectionId: promoSelection.id,
                selectionName: promoSelection.title ?? '-',
                selectionPosition: 1,
                windowSessionId: (sessionId || promoSelection.sessionId) ?? '-',
                promoType: PromoType.ITEMPROMOMOVIE,
            });
        },

        createPromoBlockAnalytics(promoSelection, position) {
            const promoBlock = promoSelection.items[position] as PromoSelectionItem;

            let title = '';
            let uuid = '';
            let trailerUuid = '';
            let uuidType: MyMoviesMovieImpressionNavigatedV8UuidType;
            let promoType: PromoType;

            if (promoBlock.type === 'movie') {
                title = promoBlock.title.toString();
                uuid = promoBlock.contentId;
                uuidType = MyMoviesMovieImpressionNavigatedV8UuidType.Ott;
                trailerUuid = promoBlock.videoContentId ?? '';
                promoType = PromoType.ITEMPROMOMOVIE;
            } else if (promoBlock.type === 'channelProgram') {
                title = promoBlock.channel.title;
                uuid = promoBlock.channel.contentId;
                uuidType = MyMoviesMovieImpressionNavigatedV8UuidType.TvChannel;
                promoType = PromoType.ITEMPROMOCHANNELPROGRAM;
            }

            return {
                itemImpressionShowed() {
                    eventSender.sendOnce(myMoviesMovieImpressionShowedV6, {
                        entityType: ImpressionWindowEntityType.PROMO,
                        uuid: uuid ?? '-',
                        title: title ?? '-',
                        uuidType: uuidType as unknown as MyMoviesMovieImpressionShowedV6UuidType,
                        cardPosition: normalizeItemPosition(position),
                        windowId: promoSelection.showcaseId ?? '-',
                        selectionId: promoSelection.id,
                        selectionName: promoSelection.title ?? '-',
                        selectionPosition: 1,
                        windowSessionId: (sessionId || promoSelection.sessionId) ?? '-',
                        promoType,
                    });
                },

                itemImpressionNavigated(to) {
                    const navigatedTo = (() => {
                        switch (to) {
                            case 'movie_card':
                                return MyMoviesMovieImpressionNavigatedV8To.MovieCardScreen;
                            case 'player':
                                return MyMoviesMovieImpressionNavigatedV8To.PlayerScreen;
                            case 'payment':
                                return MyMoviesMovieImpressionNavigatedV8To.Payment;
                            case 'channel_player':
                                return MyMoviesMovieImpressionNavigatedV8To.ChannelPlayerScreen;
                            default:
                                throw new Error(`Unknown to: ${to}`);
                        }
                    })();

                    eventSender.send(myMoviesMovieImpressionNavigatedV8, {
                        entityType: ImpressionWindowEntityType.PROMO,
                        uuid: uuid ?? '-',
                        title: title ?? '-',
                        uuidType,
                        cardPosition: normalizeItemPosition(position),
                        windowId: promoSelection.showcaseId ?? '-',
                        selectionId: promoSelection.id,
                        selectionName: promoSelection.title ?? '-',
                        selectionPosition: 1,
                        windowSessionId: (sessionId || promoSelection.sessionId) ?? '-',
                        promoType,
                        to: navigatedTo,
                    });
                },

                movieImpressionMarked(type) {
                    const markType = (() => {
                        switch (type) {
                            case 'bookmarked':
                                return ContentMarkType.Bookmarked;
                            case 'unbookmarked':
                                return ContentMarkType.Unbookmarked;
                            case 'markedAsNotInteresting':
                                return ContentMarkType.MarkedAsNotInteresting;
                            case 'unmarkedAsNotInteresting':
                                return ContentMarkType.UnmarkedAsNotInteresting;
                            default:
                                throw new Error(`Unknown type: ${type}`);
                        }
                    })();

                    eventSender.send(myMoviesMovieImpressionMarkedV3, {
                        entityType: ImpressionWindowEntityType.PROMO,
                        uuid: uuid ?? '-',
                        title: title ?? '-',
                        cardPosition: normalizeItemPosition(position),
                        windowId: promoSelection.showcaseId ?? '-',
                        selectionId: promoSelection.id,
                        selectionName: promoSelection.title ?? '-',
                        selectionPosition: 1,
                        windowSessionId: (sessionId || promoSelection.sessionId) ?? '-',
                        markType,
                    });
                },

                trailerPlayerPreviewStarted() {
                    eventSender.send(myMoviesTrailerPlayerPreviewStartedV3, {
                        entityType: transformSelectionType(promoSelection.typename ?? '-'),
                        uuid: uuid ?? '-',
                        title: title ?? '-',
                        cardPosition: normalizeItemPosition(position),
                        windowId: promoSelection.showcaseId ?? '-',
                        selectionId: promoSelection.id,
                        selectionName: promoSelection.title ?? '-',
                        selectionPosition: 1,
                        windowSessionId: (sessionId || promoSelection.sessionId) ?? '-',
                        trailerUuid: trailerUuid ?? '-',
                    });
                },

                trailerPlayerPreviewFinished() {
                    eventSender.send(myMoviesTrailerPlayerPreviewFinishedV3, {
                        entityType: transformSelectionType(promoSelection.typename ?? '-'),
                        uuid: uuid ?? '-',
                        title: title ?? '-',
                        cardPosition: normalizeItemPosition(position),
                        windowId: promoSelection.showcaseId ?? '-',
                        selectionId: promoSelection.id,
                        selectionName: promoSelection.title ?? '-',
                        selectionPosition: 1,
                        windowSessionId: (sessionId || promoSelection.sessionId) ?? '-',
                        trailerUuid: trailerUuid ?? '-',
                    });
                },

                offerShowed(offer) {
                    const { tariffOffer, optionOffers = [] } = offer?.catalogCompositeOffer || {};
                    const { batchId = '', positionId = '' } = offer?.analyticData || {};
                    const tariffName = tariffOffer?.name;
                    const optionName = optionOffers[0]?.name;

                    const offerPositionsData = getOfferPositionsData({
                        offersBatchId: batchId,
                        offersPositionId: positionId,
                    });

                    eventSender.sendOnce(myMoviesSubscriptionOfferShowedV5, {
                        entityType: MyMoviesOfferEntityType.Promoblock,
                        offerTitle: getOfferTitle(offer),
                        buttonText: offer.offerTexts.buttonText,
                        billingProductId: 'tarifficator',
                        billingProductIds: getBillingProductIds({
                            tariffOfferName: tariffName,
                            optionOfferNames: optionName,
                        }),
                        offerOptionNames: optionName ?? '',
                        offerTariffName: tariffName ?? '',
                        position: 1,
                        title,
                        uuid,
                        selectionId: promoSelection.id,
                        windowSessionId: sessionId || promoSelection.sessionId,
                        show: offerPositionsData,
                    });
                },

                offerNavigated(offer) {
                    const { tariffOffer, optionOffers = [] } = offer?.catalogCompositeOffer || {};
                    const { batchId = '', positionId = '' } = offer?.analyticData || {};
                    const tariffName = tariffOffer?.name;
                    const optionName = optionOffers[0]?.name;

                    const offerPositionsData = getOfferPositionsData({
                        offersBatchId: batchId,
                        offersPositionId: positionId,
                    });

                    eventSender.send(myMoviesSubscriptionOfferNavigatedV5, {
                        entityType: MyMoviesOfferEntityType.Promoblock,
                        offerTitle: getOfferTitle(offer),
                        buttonText: offer.offerTexts.buttonText,
                        billingProductId: 'tarifficator',
                        billingProductIds: getBillingProductIds({
                            tariffOfferName: tariffName,
                            optionOfferNames: optionName,
                        }),
                        offerOptionNames: optionName ?? '',
                        offerTariffName: tariffName ?? '',
                        position: 1,
                        title,
                        uuid,
                        selectionId: promoSelection.id,
                        windowSessionId: sessionId || promoSelection.sessionId,
                        click: offerPositionsData,
                    });
                },

                offerSucceed(offer) {
                    const { tariffOffer, optionOffers = [] } = offer?.catalogCompositeOffer || {};
                    const tariffName = tariffOffer?.name;
                    const optionName = optionOffers[0]?.name;

                    eventSender.send(myMoviesSubscriptionOfferSucceedV5, {
                        entityType: MyMoviesOfferEntityType.Promoblock,
                        offerTitle: getOfferTitle(offer),
                        buttonText: offer.offerTexts.buttonText,
                        billingProductIds: getBillingProductIds({
                            tariffOfferName: tariffName,
                            optionOfferNames: optionName,
                        }),
                        offerOptionNames: optionName ?? '',
                        offerTariffName: tariffName ?? '',
                        paymentType: PaymentTypes.PaymentWidget,
                        position: 1,
                        title,
                        uuid,
                        selectionId: promoSelection.id,
                        windowSessionId: sessionId || promoSelection.sessionId,
                    });
                },
            };
        },

        continueWatchingCardShowed(continueWatching, entity, position) {
            eventSender.sendOnce(myMoviesMovieImpressionShowedV4, {
                entityType: 'CONTINUE_WATCHING',
                uuid: entity.contentId ?? '-',
                title: entity.title.toString() ?? '-',
                cardPosition: normalizeItemPosition(position),
                windowId: continueWatching.showcaseId ?? '-',
                selectionId: continueWatching.id,
                selectionName: continueWatching.title ?? '-',
                selectionPosition: 2,
                windowSessionId: sessionId ?? '-',
            });
        },

        continueWatchingCardNavigated(continueWatching, entity, position) {
            eventSender.send(myMoviesMovieImpressionNavigatedV5, {
                entityType: 'CONTINUE_WATCHING',
                uuid: entity.contentId ?? '-',
                title: entity.title.toString() ?? '-',
                cardPosition: normalizeItemPosition(position),
                windowId: continueWatching.showcaseId ?? '-',
                selectionId: continueWatching.id,
                selectionName: continueWatching.title ?? '-',
                selectionPosition: 2,
                windowSessionId: sessionId ?? '-',
                to: MyMoviesMovieImpressionNavigatedV5To.PlayerScreen,
            });
        },

        styledShowcaseCardShowed(multiSelection, multiSelectionPosition, entity, position) {
            eventSender.sendOnce(
                multiselectionItemShowedV2,
                {
                    windowId: multiSelection.showcaseId ?? '-',
                    multiselectionId: multiSelection.key,
                    multiselectionName: multiSelection.title ?? '-',
                    multiselectionPosition: normalizeItemPosition(multiSelectionPosition),
                    selectionId: `${entity.id}/${multiSelection.showcaseId}`,
                    selectionName: entity.title ?? '-',
                    selectionPosition: normalizeItemPosition(position),
                    windowSessionId: sessionId ?? '-',
                    entityType:
                        MultiselectionItemShowedV2EntityType.MultiselectionMultiselectionItem,
                    page: ShowcasePages.MyMovies,
                    styledShowcaseId: multiSelection.id ?? '-',
                },
                { namespace: MultiselectionItemShowedNamespacesV2.MyMovies }
            );
        },

        styledShowcaseCardNavigated(multiSelection, multiSelectionPosition, entity, position) {
            eventSender.send(
                multiselectionItemNavigatedV2,
                {
                    windowId: multiSelection.showcaseId ?? '-',
                    multiselectionId: multiSelection.key,
                    multiselectionName: multiSelection.title ?? '-',
                    multiselectionPosition: normalizeItemPosition(multiSelectionPosition),
                    selectionId: `${entity.id}/${multiSelection.showcaseId}`,
                    selectionName: entity.title ?? '-',
                    selectionPosition: normalizeItemPosition(position),
                    windowSessionId: sessionId ?? '-',
                    entityType:
                        MultiselectionItemNavigatedV2EntityType.MultiselectionMultiselectionItem,
                    page: ShowcasePages.MyMovies,
                    styledShowcaseId: multiSelection.id ?? '-',
                    from: ShowcasePages.MyMovies,
                    to: SelectionsPages.Showcase,
                },
                { namespace: MultiselectionItemShowedNamespacesV2.MyMovies }
            );
        },

        createChannelProgramsAnalytics(channelPrograms, selectionPosition) {
            return {
                selectionNavigated(placement) {
                    eventSender.send(myMoviesSelectionNavigatedV3, {
                        entityType: mapSelectionNavigatedPlacement(placement),
                        selectionId: channelPrograms.id,
                        selectionName: channelPrograms.title ?? '-',
                        selectionPosition: normalizeItemPosition(selectionPosition),
                        windowSessionId: sessionId ?? '-',
                        to: MyMoviesSelectionNavigatedV3To.TvchannelsScreen,
                    });
                },

                channelImpressionShowed(channel, position, program) {
                    eventSender.sendOnce(
                        channelImpressionShowed,
                        {
                            page: ShowcasePages.MyMovies,
                            entityType: ChannelImpressionEntity.ChannelPrograms,
                            uuid: program?.id.toString() ?? '-',
                            title: program?.title ?? '-',
                            cardPosition: position,
                            windowId: channelPrograms.showcaseId ?? '-',
                            selectionId: channelPrograms.key,
                            selectionName: channelPrograms.title,
                            selectionPosition,
                            windowSessionId: sessionId ?? '-',
                            channelName: channel.title,
                            channelId: channel.contentId,
                            styledShowcaseId: '',
                        },
                        {
                            namespace: ChannelImpressionShowedNamespaces.MyMovies,
                        }
                    );
                },

                channelImpressionNavigated(channel, position, program) {
                    eventSender.send(
                        channelImpressionNavigated,
                        {
                            page: ShowcasePages.MyMovies,
                            entityType: ChannelImpressionEntity.ChannelPrograms,
                            uuid: program?.id.toString() ?? '-',
                            title: program?.title ?? '-',
                            cardPosition: position,
                            windowId: channelPrograms.showcaseId ?? '-',
                            selectionId: channelPrograms.key,
                            selectionName: channelPrograms.title,
                            selectionPosition,
                            windowSessionId: sessionId ?? '-',
                            channelName: channel.title,
                            channelId: channel.contentId,
                            styledShowcaseId: '',
                            from: ShowcasePages.MyMovies,
                        },
                        {
                            namespace: ChannelImpressionShowedNamespaces.MyMovies,
                        }
                    );
                },
            };
        },
    };
}
